import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { sendOrQueue } from '../lib/outbox.js';
import { katalogLaden, katalogSpeichern } from '../lib/artikelsuche.js';

const enc = encodeURIComponent;

// ─── Lesen ────────────────────────────────────────────────────────────────────
/**
 * Auftragsdaten (Zeit, Material, Dateien, Kommentare, Kunde) holt die App nur, wenn man den Bereich ansieht
 * (und der Stand älter als ANSEHEN_MS ist) oder «Aktualisieren» tippt – jeder Abruf kostet Exxas-Aufrufe.
 * `{ laden: false }` liest nur, was schon da ist (z. B. die Kacheln der Übersicht).
 */
export const ANSEHEN_MS = 10 * 60_000;
export const useMe = () => useQuery({ queryKey: ['me'], queryFn: () => api('/me'), staleTime: Infinity });

export const useAuftragSuche = (q) => useQuery({
  queryKey: ['auftraege', q],
  queryFn: () => api(`/auftraege?q=${enc(q)}`).then((r) => r.auftraege),
  enabled: q.length >= 2,
  placeholderData: keepPreviousData,
  staleTime: 60_000,
});

export const useAuftrag = (nr) => useQuery({
  queryKey: ['auftrag', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}`).then((r) => r.auftrag),
  staleTime: 60 * 60_000,
});

/** Montag der Woche eines Datums «YYYY-MM-DD» (Schlüssel für «Meine Stunden»). */
export function montagVon(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

/** Eigene Stunden der Woche über alle Aufträge (Exxas-Wochenzettel, 1 Aufruf pro Woche). */
export const useMeineStunden = (datum, { laden = true } = {}) => useQuery({
  queryKey: ['meineStunden', montagVon(datum)],
  queryFn: () => api(`/meine-stunden?datum=${enc(montagVon(datum))}`),
  enabled: laden,
  staleTime: ANSEHEN_MS,
});

/** Arten für «Abwesenheit beantragen» (aus den App-Settings der Firma). */
export const useAbwesenheitsArten = () => useQuery({
  queryKey: ['abwesenheitsArten'],
  queryFn: () => api('/abwesenheiten/arten').then((r) => r.arten),
  staleTime: 24 * 60 * 60_000,
});

/** Abwesenheit in Exxas beantragen (nur online; Idempotency-Key gegen Doppel). */
export function useAbwesenheitBeantragen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, ...body }) => api('/abwesenheiten', { method: 'POST', body, idempotencyKey: key }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['meineStunden'] }),
  });
}

/** Woche in Exxas zur Genehmigung einreichen (nur online; mit Idempotency-Key gegen Doppel im Funkloch). */
export function useWocheEinreichen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jahr, kw, kommentar, key }) => api('/meine-stunden/einreichen', { method: 'POST', body: { jahr, kw, kommentar }, idempotencyKey: key }),
    onSuccess: (_r, { montag }) => wocheNachfuehren(qc, montag, (w) => ({ ...w, status: w.status || 'eingereicht', eingereicht: true })),
  });
}

/** Einreichung der Woche zurückziehen (Woche wieder offen). */
export function useWocheZuruecksetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jahr, kw, key }) => api('/meine-stunden/zuruecksetzen', { method: 'POST', body: { jahr, kw }, idempotencyKey: key }),
    onSuccess: (_r, { montag }) => wocheNachfuehren(qc, montag, (w) => ({ ...w, status: null, eingereicht: false, genehmigt: false })),
  });
}

/** Neuen Stand der Woche sofort zeigen (Sperr-Hinweis, Wochenrapport), dann bei Exxas nachladen. */
function wocheNachfuehren(qc, montag, aendern) {
  qc.setQueryData(['meineStunden', montag], (w) => (w ? aendern(w) : w));
  return qc.invalidateQueries({ queryKey: ['meineStunden', montag] });
}

/** Nach einer Buchung «Meine Stunden» direkt nachführen – ohne den Wochenzettel neu bei Exxas zu holen. */
export function stundenNachfuehren(qc, { datum, stunden, nummer, titel }) {
  if (!datum || !(stunden > 0)) return;
  qc.setQueryData(['meineStunden', montagVon(datum)], (w) => {
    if (!w?.tage) return w;
    const tage = w.tage.map((t) => (t.datum === datum
      ? { ...t, stunden: Math.round((t.stunden + stunden) * 100) / 100, eintraege: [...t.eintraege, { dokId: null, nummer, titel: titel || nummer, kunde: null, beschreibung: '', stunden }] }
      : t));
    return { ...w, tage, total: Math.round(tage.reduce((n, t) => n + t.stunden, 0) * 100) / 100 };
  });
}

export const useLeistungen = (nr, { laden = true } = {}) => useQuery({
  queryKey: ['leistungen', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/leistungen`).then((r) => r.leistungen),
  enabled: laden,
  staleTime: ANSEHEN_MS,
});

export const useMaterial = (nr, { laden = true } = {}) => useQuery({
  queryKey: ['material', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/material`).then((r) => r.positionen),
  enabled: laden,
  staleTime: ANSEHEN_MS,
});

export const useDateien = (nr, { laden = true } = {}) => useQuery({
  queryKey: ['dateien', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/dateien`).then((r) => r.dateien),
  enabled: laden,
  staleTime: ANSEHEN_MS,
});

export const useKommentare = (nr, { laden = true } = {}) => useQuery({
  queryKey: ['kommentare', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/kommentare`).then((r) => r.kommentare),
  enabled: laden,
  staleTime: ANSEHEN_MS,
});


export const useZeitplan = (nr, { laden = true } = {}) => useQuery({
  queryKey: ['zeitplan', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/zeitplan`).then((r) => r.zeitplan),
  staleTime: 5 * 60_000,
  enabled: laden,
});

/** Kundeninfos (Exxas-Kunde des Auftrags) – erst beim Ansehen geladen, offline aus der Gerätekopie. */
export const useKunde = (nr) => useQuery({
  queryKey: ['kunde', nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/kunde`).then((r) => r.kunde),
  staleTime: ANSEHEN_MS,
  retry: (n, err) => n < 2 && ![403, 404].includes(err?.status),
});

/** Speichert geänderte Kundenfelder ({ feld: { alt, neu } }) – nur online, ohne Outbox (Konflikte müssen sichtbar sein). */
export function useSaveKunde(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (aenderungen) => api(`/auftraege/${enc(nr)}/kunde`, { method: 'PUT', body: { aenderungen } }),
    onSuccess: (res) => qc.setQueryData(['kunde', nr], res.kunde),
  });
}

/** Leistungsarten aus Exxas (Artikel mit leistungAktiv); 1 h frisch, offline aus der Gerätekopie. */
export const useLeistungsarten = ({ laden = true } = {}) => useQuery({
  queryKey: ['leistungsarten'],
  queryFn: () => api('/leistungsarten').then((r) => r.leistungsarten),
  staleTime: 24 * 60 * 60_000,
  enabled: laden,
});

/**
 * Vorauswahl der Leistungsart: zuletzt verwendet, falls in der aktuellen Exxas-Liste, sonst der erste Eintrag.
 * @param {number|null} gewuenscht
 */
export function gueltigeLeistungsart(arten, gewuenscht) {
  if (!arten?.length) return gewuenscht ?? null;
  return arten.some((a) => a.id === gewuenscht) ? gewuenscht : arten[0].id;
}

/**
 * Artikelkatalog (alle Material-Artikel) für die Suche im Frontend. Startet aus der Gerätekopie (bleibt auch nach
 * dem Abmelden), fragt höchstens 1× pro Tag beim eigenen Server nach – Exxas selbst nur bei «Aktualisieren».
 */
const KATALOG_KEY = ['artikelkatalog'];
const holeKatalog = async (frisch = false) => {
  const k = await api(`/artikel/katalog${frisch ? '?frisch=1' : ''}`, { timeoutMs: 60_000 });
  katalogSpeichern(k);
  return k;
};
export const useArtikelKatalog = () => useQuery({
  queryKey: KATALOG_KEY,
  queryFn: () => holeKatalog(),
  initialData: () => katalogLaden()?.katalog,
  initialDataUpdatedAt: () => katalogLaden()?.geladenAm,
  staleTime: 24 * 60 * 60_000,
  refetchOnWindowFocus: false,
});

/** «Aktualisieren»: Stamm sofort neu aus Exxas (z. B. nach dem Anlegen eines Artikels). */
export function useKatalogAktualisieren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => holeKatalog(true),
    onSuccess: (k) => qc.setQueryData(KATALOG_KEY, k),
  });
}

/** Serversuche – nur noch als Rückfall (Katalog fehlt, Barcode nicht im Katalog). */
export const useArtikelSuche = (q) => useQuery({
  queryKey: ['artikel', q],
  queryFn: () => api(`/artikel?q=${enc(q)}`).then((r) => r.artikel),
  enabled: q.length >= 2,
  placeholderData: keepPreviousData,
  staleTime: 5 * 60_000,
});

/** Material-Favoriten { artikel, fehlend } – immer vorhanden (Gerätekopie), 1 h frisch. */
export const useFavoriten = () => useQuery({
  queryKey: ['favoriten'],
  queryFn: () => api('/artikel/favoriten'),
  staleTime: 60 * 60_000,
});

// ─── Schreiben ────────────────────────────────────────────────────────────────
/**
 * Zeit buchen – ohne Netz landet die Buchung in der Outbox.
 * Ergebnis: { queued: false, res: { erstellt } } oder { queued: true, id }
 */
export function useCreateLeistung(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ label, ...body }) => sendOrQueue({
      kind: 'leistung',
      nummer: nr,
      uid: qc.getQueryData(['me'])?.uid ?? null,
      label: label ?? `${body.stunden} h ${body.beschreibung || 'Arbeitszeit'}`,
      path: `/auftraege/${enc(nr)}/leistungen`,
      body,
    }),
    onSuccess: (r, vars) => {
      // neue Buchung direkt in die Liste – ohne die Liste neu bei Exxas zu holen (vorgemerkte: nach dem Senden)
      const neu = r?.res?.leistung;
      if (!r?.queued) stundenNachfuehren(qc, { datum: vars.datum, stunden: vars.stunden, nummer: nr, titel: qc.getQueryData(['auftrag', nr])?.titel });
      if (neu?.id) qc.setQueryData(['leistungen', nr], (list) => (list ? [neu, ...list.filter((l) => l.id !== neu.id)] : list));
      else if (!r?.queued) qc.invalidateQueries({ queryKey: ['leistungen', nr] });
    },
  });
}

export function useUpdateLeistung(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api(`/auftraege/${enc(nr)}/leistungen/${enc(id)}`, { method: 'PATCH', body }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['leistungen', nr] }),
  });
}

export function useDeleteLeistung(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api(`/auftraege/${enc(nr)}/leistungen/${enc(id)}`, { method: 'DELETE' }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['leistungen', nr] }),
  });
}

export function useUploadDatei(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api(`/auftraege/${enc(nr)}/dateien`, { method: 'POST', body }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['dateien', nr] }),
  });
}

export function useCreateKommentar(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api(`/auftraege/${enc(nr)}/kommentare`, { method: 'POST', body }),
    onSuccess: (res) => {
      // neuen Kommentar direkt anzeigen statt die Liste neu bei Exxas zu holen
      if (res?.kommentar) qc.setQueryData(['kommentare', nr], (list) => [res.kommentar, ...(list ?? [])]);
      else qc.invalidateQueries({ queryKey: ['kommentare', nr] });
      // Foto liegt jetzt unter Dateien → beim nächsten Ansehen neu laden (nicht sofort)
      if (res?.kommentar?.foto) qc.invalidateQueries({ queryKey: ['dateien', nr], refetchType: 'none' });
    },
  });
}



/**
 * Regieauftrag anlegen (neuer Exxas-Auftrag auf denselben Kunden). Ein neuer Auftrag hat noch nichts gebucht –
 * die Listen werden leer vorbelegt, damit «Erfassen» sie nicht erst bei Exxas holt.
 */
export function useCreateRegie(nr) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ titel, beschreibung, key }) => api(`/auftraege/${enc(nr)}/regie`, { method: 'POST', body: { titel, beschreibung }, idempotencyKey: key }),
    onSuccess: (r) => {
      const a = r?.auftrag;
      if (!a?.nummer) return;
      qc.setQueryData(['auftrag', a.nummer], a);
      for (const k of ['leistungen', 'material', 'dateien', 'kommentare']) qc.setQueryData([k, a.nummer], []);
    },
  });
}

/** Kunden suchen (für «Regierapport erstellen»): Name, Nummer, Adresse. Erst ab 2 Zeichen. */
export const useKundenSuche = (q) => useQuery({
  queryKey: ['kunden', q],
  queryFn: () => api(`/kunden?q=${enc(q)}`).then((r) => r.kunden),
  enabled: q.length >= 2,
  placeholderData: keepPreviousData,
  staleTime: 5 * 60_000,
});

/** Regieauftrag direkt für einen Kunden (ohne bestehenden Auftrag). */
export function useCreateRegieFuerKunde() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kundeId, titel, beschreibung, key }) => api('/regie', { method: 'POST', body: { kundeId, titel, beschreibung }, idempotencyKey: key }),
    onSuccess: (r) => {
      const a = r?.auftrag;
      if (!a?.nummer) return;
      qc.setQueryData(['auftrag', a.nummer], a);
      for (const k of ['leistungen', 'material', 'dateien', 'kommentare']) qc.setQueryData([k, a.nummer], []);
    },
  });
}

/**
 * Excel-Dokumente eines Auftrags (je eine Excel in den Exxas-Dateien): Übersichtslisten (`checklisten`) und
 * Bewässerungspläne (`plaene`). Liste: { listen: [{ dateiId, name, titel }], baustelle, team }.
 */
const DOK_KEYS = { checklisten: ['checklisten', 'checkliste'], plaene: ['plaene', 'plan'] };
/** Formulare aus Vorlagen: art «formulare/<id>» → eigene Schlüssel pro Vorlage. */
export const dokKeys = (art) => DOK_KEYS[art] ?? [`formularListe:${art}`, `formular:${art}`];
export const useExcelDokumente = (art, nr, { laden = true, frisch = false } = {}) => useQuery({
  queryKey: [dokKeys(art)[0], nr],
  queryFn: () => api(`/auftraege/${enc(nr)}/${art}`),
  enabled: laden,
  staleTime: ANSEHEN_MS,
  refetchOnMount: frisch ? 'always' : true, // frisch: z. B. Vorlagen beim Anlegen – auch kurz zuvor gespeicherte zeigen
});
/** Ein Dokument: { daten, datei, version, team }. `dateiId` «neu» = noch nicht angelegt (nichts zu laden). */
export const useExcelDokument = (art, nr, dateiId) => useQuery({
  queryKey: [dokKeys(art)[1], nr, dateiId],
  queryFn: () => api(`/auftraege/${enc(nr)}/${art}/${enc(dateiId)}`),
  enabled: !!dateiId && dateiId !== 'neu',
  staleTime: ANSEHEN_MS,
});
/** Speichern: neues Dokument anlegen (POST) oder bestehendes ersetzen (PUT, mit Stand beim Öffnen). */
export function useSaveExcelDokument(art, nr) {
  const qc = useQueryClient();
  const [listeKey, dokKey] = dokKeys(art);
  return useMutation({
    mutationFn: ({ dateiId, daten, version, key }) => (dateiId && dateiId !== 'neu'
      ? api(`/auftraege/${enc(nr)}/${art}/${enc(dateiId)}`, { method: 'PUT', body: { daten, version }, idempotencyKey: key })
      : api(`/auftraege/${enc(nr)}/${art}`, { method: 'POST', body: { daten }, idempotencyKey: key })),
    onSuccess: (r) => {
      qc.setQueryData([dokKey, nr, r.datei.id], { daten: r.daten, datei: r.datei, version: r.version, team: r.team });
      const eintrag = { dateiId: r.datei.id, name: r.datei.name, titel: r.daten.titel };
      qc.setQueryData([listeKey, nr], (alt) => (alt
        ? { ...alt, listen: alt.listen.some((l) => l.dateiId === eintrag.dateiId) ? alt.listen.map((l) => (l.dateiId === eintrag.dateiId ? eintrag : l)) : [...alt.listen, eintrag] }
        : alt));
      qc.invalidateQueries({ queryKey: ['dateien', nr], refetchType: 'none' }); // Dateiliste beim nächsten Ansehen neu
    },
  });
}
export const useChecklisten = (nr, opts) => useExcelDokumente('checklisten', nr, opts);
export const useCheckliste = (nr, dateiId) => useExcelDokument('checklisten', nr, dateiId);
export const useSaveCheckliste = (nr) => useSaveExcelDokument('checklisten', nr);
export const usePlaene = (nr, opts) => useExcelDokumente('plaene', nr, opts);
export const usePlan = (nr, dateiId) => useExcelDokument('plaene', nr, dateiId);
export const useSavePlan = (nr) => useSaveExcelDokument('plaene', nr);

// ─── Verwaltung (nur Firmenadmins) ────────────────────────────────────────────
// Nicht offline gespeichert (fehlt in PERSIST): immer der aktuelle Stand vom Server.

export const useVerwaltungEinstellungen = () => useQuery({ queryKey: ['verwaltung', 'einstellungen'], queryFn: () => api('/verwaltung/einstellungen'), staleTime: 0 });
export const useVerwaltungBegriffe = () => useQuery({ queryKey: ['verwaltung', 'begriffe'], queryFn: () => api('/verwaltung/begriffe'), staleTime: 0 });
export const useVerwaltungProtokoll = () => useQuery({ queryKey: ['verwaltung', 'protokoll'], queryFn: () => api('/verwaltung/protokoll'), staleTime: 0 });

/** Einstellungen speichern: { NAME: wert | null } (null = zurück auf Standard). */
export function useEinstellungenSpeichern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (werte) => api('/verwaltung/einstellungen', { method: 'PUT', body: { werte } }),
    onSuccess: (res) => {
      qc.setQueryData(['verwaltung', 'einstellungen'], res);
      qc.invalidateQueries({ queryKey: ['verwaltung', 'protokoll'] });
      // Rollen (Projektleiter) und Team der Listen können sich geändert haben
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['checklisten'] });
      qc.invalidateQueries({ queryKey: ['plaene'] });
    },
  });
}

/** Begriffe speichern: { schluessel: text | null } – die App übernimmt sie sofort (über /api/me). */
export function useBegriffeSpeichern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (werte) => api('/verwaltung/begriffe', { method: 'PUT', body: { werte } }),
    onSuccess: (res) => {
      qc.setQueryData(['verwaltung', 'begriffe'], res);
      qc.invalidateQueries({ queryKey: ['verwaltung', 'protokoll'] });
      qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ─── Apps (Module) ───────────────────────────────────────────────────────────

/** Katalog der Module mit Zustand für die Firma: { module: [...], darfSchalten, darfBeantragen }. */
export const useModule = ({ laden = true } = {}) => useQuery({ queryKey: ['module'], queryFn: () => api('/module'), enabled: laden, staleTime: 60_000 });

/**
 * Aktion an einem Modul: { id, art: 'beantragen' | 'zurueckziehen' | 'schalten' | 'interesse' | 'keinInteresse', an?, nachricht? }.
 * Danach gelten die Funktionen der Firma neu (Navigation) → /api/me neu laden.
 */
export function useModulAktion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, art, an, nachricht }) => {
      const pfad = `/module/${enc(id)}`;
      if (art === 'beantragen') return api(`${pfad}/anfrage`, { method: 'POST', body: { nachricht } });
      if (art === 'zurueckziehen') return api(`${pfad}/anfrage`, { method: 'DELETE' });
      if (art === 'schalten') return api(`${pfad}/schalten`, { method: 'POST', body: { an } });
      return api(`${pfad}/interesse`, { method: art === 'interesse' ? 'POST' : 'DELETE' });
    },
    onSuccess: (res, { art }) => {
      qc.setQueryData(['module'], res);
      if (art === 'schalten') qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

// ─── Formulare aus Vorlagen ───────────────────────────────────────────────────

/** Vorlagen der Firma (Kopfdaten) – für «Mehr» und Routen. Ändern sich nur mit einem Deployment oder Modul-Schalter. */
export const useFormularVorlagen = ({ laden = true } = {}) => useQuery({ queryKey: ['formulare'], queryFn: () => api('/formulare').then((r) => r.vorlagen), enabled: laden, staleTime: 60 * 60_000 });
/** Ganze Vorlage (für den Editor); offline aus der Gerätekopie. */
export const useFormularVorlage = (id) => useQuery({ queryKey: ['formularVorlage', id], queryFn: () => api(`/formulare/${enc(id)}`).then((r) => r.vorlage), enabled: !!id, staleTime: 60 * 60_000 });
